import { useEffect } from 'react'
import { PackageCheck } from 'lucide-react'
import { OverviewStatTile } from '@/components/overview/OverviewStatTile'
import { PACK_PREPARATION_COPY } from '@/lib/pack-readiness'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'

/**
 * Signals-bar tile for the Submission Pack: how many checklist items are prepared,
 * and how many blockers stand between the bid and a submittable pack.
 *
 * Loads the pack store on mount. The Pack page loads it too, but the two never
 * mount together (they are sibling routes), so this is one extra GET per visit
 * to Overview rather than a fight over the same state.
 */
export function PackSnapshot({ bidId }: { bidId: string }) {
  const load = useSubmissionPackStore((s) => s.load)
  const pack = useSubmissionPackStore((s) => s.pack)
  const storeBidId = useSubmissionPackStore((s) => s.bidId)
  const isLoading = useSubmissionPackStore((s) => s.isLoading)

  useEffect(() => {
    void load(bidId)
  }, [bidId, load])

  // The store is global, so a pack left over from another bid must not be read
  // as this one's while the fetch for this bid is still in flight.
  const current = storeBidId === bidId ? pack : null

  // `isLoading` alone is false on the very first render — the fetch has not been
  // dispatched yet — so the tile would flash the first-run hint at a bid that
  // already has a pack. A store still pointing at another bid is just as
  // pending as one mid-fetch.
  const pending = isLoading || storeBidId !== bidId
  const prepared = current?.rollup.ready ?? 0
  const total = current?.rollup.total ?? 0

  const b = current?.blockers
  const blockerCount = b
    ? b.mandatoryAtRisk +
      b.missingHandovers +
      b.undraftedResponses +
      b.unacknowledged +
      b.overWordLimit +
      b.openTodos +
      b.removedByAddendum
    : 0

  // Empty is the first-run case, so the tile stays put and points at the next
  // step instead of hiding — same convention as ResponseProgress.
  const settledSub =
    total === 0
      ? 'Run Analysis or add an item'
      : blockerCount > 0
        ? `${blockerCount} ${blockerCount === 1 ? 'blocker' : 'blockers'}`
        : undefined

  return (
    <OverviewStatTile
      icon={PackageCheck}
      label="Submission Pack"
      value={PACK_PREPARATION_COPY.overviewTileValue(prepared, total)}
      // `loading` only gates the tile's value, so the sub line has to be held
      // back here too — mid-fetch the counts are still zero and the tile would
      // otherwise flash the first-run hint at a bid that already has a pack.
      sub={pending ? undefined : settledSub}
      progress={total > 0 ? (prepared / total) * 100 : undefined}
      to={`/app/bids/${bidId}/submit`}
      loading={pending}
    />
  )
}
